import { supabase } from '../../config/supabaseClient';
import { API_BASE_URL } from '../../config';
import {
  AppError,
  ErrorCategory,
  createAppError,
  handleHttpError,
  withErrorHandling
} from '../../utils/errorHandler';

export interface CRMRequestOptions<TBody = unknown> extends Omit<RequestInit, 'body'> {
  body?: TBody;
  description?: string;
  context?: Record<string, unknown>;
  notifyUserOnError?: boolean;
}

export interface CRMErrorPayload {
  error?: string;
  message?: string;
  code?: string | number;
  details?: unknown;
}

export interface CRMResponse<T> {
  data: T;
  error?: CRMErrorPayload | null;
}

const cleanPath = (path: string) => {
  const formatted = path.trim();
  if (!formatted) {
    throw createAppError(
      'CRM API path cannot be empty',
      ErrorCategory.VALIDATION
    );
  }
  return formatted.startsWith('/') ? formatted : `/${formatted}`;
};

export const buildApiUrl = (
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>
) => {
  const formattedPath = cleanPath(path);
  const base = (API_BASE_URL || '').trim();
  if (!base) {
    throw createAppError(
      'CRM API base URL is not configured',
      ErrorCategory.CONFIGURATION
    );
  }
  const url = new URL(`${base}${formattedPath}`);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      const stringValue = String(value);
      if (stringValue.length === 0) return;
      url.searchParams.set(key, stringValue);
    });
  }
  return url.toString();
};

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  const data = (sessionResp as any)?.data || {};
  return data.session?.access_token || '';
};

const getUserId = async (): Promise<string> => {
  const userResp = await supabase.auth.getUser();
  const userData = (userResp as any)?.data;
  const userId = userData?.user?.id;
  if (!userId) {
    throw createAppError(
      'User authentication required',
      ErrorCategory.AUTHENTICATION,
      401,
      'You need to log in to continue.'
    );
  }
  return userId;
};

const normalizeError = async (response: Response): Promise<AppError> => {
  let payload: CRMErrorPayload | undefined;
  try {
    const text = await response.text();
    payload = text ? JSON.parse(text) : undefined;
  } catch (err) {
    payload = undefined;
  }

  if (payload) {
    return createAppError(
      payload.error || payload.message || 'CRM request failed',
      ErrorCategory.API,
      response.status,
      undefined,
      {
        code: payload.code,
        details: payload.details,
        url: response.url
      }
    );
  }

  return handleHttpError(response);
};

const parseRequestBody = (body: unknown): Record<string, unknown> => {
  if (body === undefined || body === null) return {};
  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as Record<string, unknown>;
    } catch (err) {
      throw createAppError(
        'CRM request body must be valid JSON when provided as string',
        ErrorCategory.VALIDATION
      );
    }
  }
  if (body instanceof FormData) {
    throw createAppError(
      'FormData is not supported for CRM requests',
      ErrorCategory.VALIDATION
    );
  }
  if (typeof body === 'object') {
    return { ...(body as Record<string, unknown>) };
  }
  throw createAppError(
    'Unsupported CRM request body type',
    ErrorCategory.VALIDATION
  );
};

const resolveRequestInit = async <TBody>(options: CRMRequestOptions<TBody>) => {
  const [token, userId] = await Promise.all([getToken(), getUserId()]);
  const method = (options.method || 'GET').toUpperCase();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'x-user-id': userId,
    ...(options.headers || {})
  };

  if (method === 'GET') {
    return { method, headers, userId } as const;
  }

  const bodyPayload = parseRequestBody(options.body);
  const bodyWithContext = JSON.stringify({ ...bodyPayload, userId });
  return { method, headers, body: bodyWithContext, userId } as const;
};

export const fetchJSON = async <TData = unknown, TBody = unknown>(
  url: string,
  options: CRMRequestOptions<TBody> = {}
): Promise<TData> => {
  const {
    description,
    context,
    notifyUserOnError = true,
    body,
    ...requestOptions
  } = options;

  return withErrorHandling(async () => {
    const init = await resolveRequestInit({ ...requestOptions, body });
    let finalUrl = url;
    try {
      const u = new URL(finalUrl);
      u.searchParams.set('userId', init.userId);
      finalUrl = u.toString();
    } catch (err) {
      throw createAppError(
        'Invalid CRM API URL',
        ErrorCategory.CONFIGURATION,
        undefined,
        undefined,
        { url: finalUrl }
      );
    }

    const response = await fetch(finalUrl, {
      ...requestOptions,
      method: init.method,
      headers: init.headers,
      ...(init.method !== 'GET' ? { body: init.body } : {})
    });

    if (!response.ok) {
      throw await normalizeError(response);
    }

    try {
      return await response.json();
    } catch (err) {
      throw createAppError(
        'Failed to parse CRM response JSON',
        ErrorCategory.API,
        undefined,
        undefined,
        { url: response.url }
      );
    }
  },
  {
    url,
    description,
    ...context
  },
  notifyUserOnError);
};

export const validateRequiredParams = (
  params: unknown,
  required: string[],
  context?: Record<string, unknown>
) => {
  if (!params || typeof params !== 'object') {
    throw createAppError(
      'CRM parameter validation requires an object payload',
      ErrorCategory.VALIDATION,
      400,
      'Invalid request payload.',
      context
    );
  }

  const source = params as Record<string, unknown>;
  const missing = required.filter((key) => {
    const value = source[key];
    return value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);
  });

  if (missing.length > 0) {
    throw createAppError(
      `Missing required parameter(s): ${missing.join(', ')}`,
      ErrorCategory.VALIDATION,
      400,
      'Required information is missing.',
      context
    );
  }
};

